import { FormProvider, getFormProps } from '@conform-to/react'
import { IconButton, Tooltip } from '@radix-ui/themes'
import { animated, useSpring } from '@react-spring/web'
import { Form, useLocation, useOutletContext } from '@remix-run/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { RxHeart, RxHeartFilled } from 'react-icons/rx'
import { useHydrated } from 'remix-utils/use-hydrated'

import {
	FormDefaults,
	FormDefaultsHiddenField,
} from '#app/components/common/form-defaults.tsx'
import {
	ADD_TO_WISHLIST_FORM,
	ADD_TO_WISHLIST_INPUTS,
} from '#app/components/forms/add-to-wishlist-form/add-to-wishlist-form.tsx'
import {
	REMOVE_FROM_WISHLIST_FORM,
	REMOVE_FROM_WISHLIST_INPUTS,
} from '#app/components/forms/remove-from-wishlist-form/remove-from-wishlist-form.tsx'
import { type GetProduct } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import {
	ROOT_APP_VIEW,
	type Context,
} from '#app/routes/_layout/route-config.ts'
import { testId } from '#app/utils/client/test-id.ts'
import { useAddToWishlistForm } from '#app/utils/hooks/forms/use-add-to-wishlist-form.ts'
import { useRemoveFromWishlistForm } from '#app/utils/hooks/forms/use-remove-from-wishlist-form.ts'

/* -------------------------------------------------------------------------------------------------
 * WishlistAction
 * -----------------------------------------------------------------------------------------------*/

enum TEST_IDS {
	wishlistAction = 'wishlist-action',
}

type WishlistActionProps = {
	product: GetProduct
	inWishlist: boolean
}

const wishlistActionI18next = {
	uiWishlistAction: 'ui-wishlist-action',
}

const WishlistAction: React.FC<WishlistActionProps> = (props) => {
	const { product, inWishlist } = props

	const { setApp, locale, loggedIn } = useOutletContext<Context>()
	const location = useLocation()
	const isHydrated = useHydrated()

	const {
		addToWishlistFetcher,
		addToWishlistFormValues,
		addToWishlistForm,
		addToWishlistFields,
	} = useAddToWishlistForm({ product })

	const {
		removeFromWishlistFetcher,
		removeFromWishlistFormValues,
		removeFromWishlistForm,
		removeFromWishlistFields,
	} = useRemoveFromWishlistForm({ product })

	const optimisticInWishlist = !addToWishlistFetcher.formData
		? !removeFromWishlistFetcher.formData
			? inWishlist
			: false
		: true

	const { t: tUI } = useTranslation(wishlistActionI18next.uiWishlistAction)
	const [hover, setHover] = React.useState(false)
	const [springState, toggle] = React.useState(true)

	const { x } = useSpring({
		from: { x: 1 },
		x: springState ? 1 : 0,
		config: { duration: 1000 },
	})

	return (
		<FormProvider
			context={
				optimisticInWishlist
					? removeFromWishlistForm.context
					: addToWishlistForm.context
			}
		>
			<Form
				method="post"
				action={`/${locale}${
					optimisticInWishlist
						? REMOVE_FROM_WISHLIST_FORM.action
						: ADD_TO_WISHLIST_FORM.action
				}${location.search}`}
				navigate={false}
				fetcherKey={
					optimisticInWishlist
						? REMOVE_FROM_WISHLIST_FORM.generateFetcherKey!({
								id: product.slug!,
							})
						: ADD_TO_WISHLIST_FORM.generateFetcherKey!({
								id: product.slug!,
							})
				}
				{...getFormProps(
					optimisticInWishlist ? removeFromWishlistForm : addToWishlistForm,
				)}
				preventScrollReset
				{...testId(TEST_IDS.wishlistAction)}
			>
				<FormDefaults
					form={
						optimisticInWishlist ? removeFromWishlistForm : addToWishlistForm
					}
					fields={
						optimisticInWishlist
							? removeFromWishlistFields
							: addToWishlistFields
					}
					values={
						optimisticInWishlist
							? removeFromWishlistFormValues
							: addToWishlistFormValues
					}
				/>
				<FormDefaultsHiddenField
					meta={
						optimisticInWishlist
							? removeFromWishlistFields[REMOVE_FROM_WISHLIST_INPUTS.productId]
							: addToWishlistFields[ADD_TO_WISHLIST_INPUTS.productId]
					}
				/>
				<animated.div
					style={{
						scale: x.to({
							range: [0, 0.25, 0.35, 0.45, 0.55, 0.65, 0.75, 1],
							output: [1, 0.87, 0.8, 1.2, 0.6, 1.2, 1.13, 1],
						}),
					}}
				>
					<Tooltip
						content={
							loggedIn
								? optimisticInWishlist
									? tUI('button.removeFromWishlist')
									: tUI('button.addToWishlist')
								: tUI('button.pleaseLogInToUseWishlistFeature')
						}
					>
						<IconButton
							className="app-WishlistButton m-2"
							size="3"
							variant="ghost"
							type={loggedIn ? 'submit' : 'button'}
							onMouseEnter={() => setHover(true)}
							onFocus={() => setHover(true)}
							onMouseLeave={() => setHover(false)}
							onBlur={() => setHover(false)}
							onClick={() => {
								if (!loggedIn) {
									setApp((prev) => ({
										...prev,
										modalView: ROOT_APP_VIEW.loginView,
										displayModal: true,
										options: {
											...prev.options,
											redirect: location.pathname,
											noJs: String(!isHydrated),
										},
									}))
								} else {
									toggle((prev) => !prev)
									setHover(false)
								}
							}}
						>
							{loggedIn &&
							((!hover && optimisticInWishlist) ||
								(hover && !optimisticInWishlist)) ? (
								<RxHeartFilled size="1.4em" />
							) : (
								<RxHeart size="1.4em" />
							)}
						</IconButton>
					</Tooltip>
				</animated.div>
			</Form>
		</FormProvider>
	)
}
WishlistAction.displayName = 'WishlistAction'

/* -----------------------------------------------------------------------------------------------*/

export {
	WishlistAction,
	wishlistActionI18next,
	TEST_IDS as WISHLIST_ACTION_TEST_IDS,
}
